import { useEffect, useState, type FormEvent } from "react"
import { Navigate, useSearchParams } from "react-router-dom"
import { useAuth } from "@/lib/auth"
import { rememberPostLoginPath } from "@/lib/post-login"

export function LoginPage() {
  const { user, loading, error, signInWithGoogle, signInWithPassword, clearError } = useAuth()
  const [showEmailForm, setShowEmailForm] = useState(false)
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [searchParams] = useSearchParams()
  const nextParam = searchParams.get("next")

  useEffect(() => {
    rememberPostLoginPath(nextParam)
  }, [nextParam])

  if (user && !loading) {
    return <Navigate to="/" replace />
  }

  async function handleGoogleSignIn() {
    await signInWithGoogle()
  }

  async function handlePasswordSubmit(e: FormEvent) {
    e.preventDefault()
    if (!email || !password) return
    setSubmitting(true)
    await signInWithPassword(email, password)
    setSubmitting(false)
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="w-full max-w-sm space-y-6 rounded-xl bg-white p-8 shadow-lg border">
        <div className="text-center">
          <img
            src="/app-icon.png"
            alt="Offseason"
            className="mx-auto mb-4 h-14 w-14 rounded-xl object-cover"
          />
          <h1 className="text-xl font-semibold text-gray-900 tracking-tight">
            Offseason Admin
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Sign in with your team account
          </p>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3">
            <p className="text-sm text-red-700">{error}</p>
            <button
              onClick={clearError}
              className="mt-1 text-xs text-red-500 underline hover:text-red-700"
            >
              Dismiss
            </button>
          </div>
        )}

        <button
          onClick={handleGoogleSignIn}
          disabled={loading}
          className="flex w-full items-center justify-center gap-3 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-xs transition-colors hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
          Sign in with Google
        </button>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200" />
          </div>
          <div className="relative flex justify-center text-xs">
            <button
              onClick={() => {
                setShowEmailForm(!showEmailForm)
                clearError()
              }}
              className="bg-white px-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              {showEmailForm ? "Hide email login" : "Or use email & password"}
            </button>
          </div>
        </div>

        {showEmailForm && (
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@offseasonwellness.com"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-xs placeholder:text-gray-400 focus:border-primary focus:outline-hidden focus:ring-1 focus:ring-primary"
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your password"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-xs placeholder:text-gray-400 focus:border-primary focus:outline-hidden focus:ring-1 focus:ring-primary"
              />
            </div>

            <button
              type="submit"
              disabled={submitting || loading}
              className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground shadow-xs transition-colors hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Signing in..." : "Sign in"}
            </button>
          </form>
        )}

        <p className="text-center text-xs text-gray-400">
          Restricted to @offseasonwellness.com accounts
        </p>
      </div>
    </div>
  )
}
